import { FundOfferBaseEntity } from '@genial/services';
import {
  ResponsesFundCategoriesByAdvisorResponse,
  ResponsesFundOfferCategoriesResponse,
  ResponsesFundOfferDetailsResponse,
  ResponsesFundOfferResponse,
  ResponsesFundOfferV2Response,
  ResponsesProductBlockResponse,
  ResponsesRedemptionFundDetailsResponse,
} from '@genial/services/dist/models';
import type {
  MetaFilterResponseFundFiltersOffersFilters,
  ResponsesFundComparisonResponse,
} from '@genial/services/dist/models';
import type { ResponsesFundRedemptionResponse } from '@genial/services/src/models';
import { Utils } from '@genial/utils';

import { metaFilterToOptions } from '../../../utils/metaFilterToOptions';
import {
  DynamicCustomFiltersProps,
  FundComparatorResultNormalized,
  FundOfferDetailEntityNormalized,
  FundOfferEntityNormalized,
  type DynamicProductCaptionsGetter,
  type FundRedemptionOfferNormalized,
} from '../types';

import { filterHasOptions, getProductCaptions, normalizeCustomFilter } from '.';

export enum FundStatus {
  OPEN = 1,
  CLOSED = 2,
  CLOSED_FOR_INVESTMENT = 3,
  CLOSED_FOR_INVESTMENT_SC = 4,
  CLOSED_FOR_REDEMPTION = 5,
}

export const closedFundMessage =
  'Este ativo está temporariamente fechado.\nTente novamente mais tarde.';
export const zeroApplicationMinimumMessage = 'Informe o valor da aplicação para continuar';
export const closedFundForInvestmentStatus = [
  FundStatus.CLOSED,
  FundStatus.CLOSED_FOR_INVESTMENT,
  FundStatus.CLOSED_FOR_INVESTMENT_SC,
];
export const closedFundForRedemptionStatus = [FundStatus.CLOSED, FundStatus.CLOSED_FOR_REDEMPTION];

const normalizeFundOffer = (offer: Partial<FundOfferBaseEntity>): FundOfferEntityNormalized => {
  return {
    ...offer,
    productIdForTerm: parseInt(offer.productId || '0', 10),
    profitabilityTwelveMonthsNormalized: Utils.Mask.Percentage(
      offer.profitabilityTwelveMonthsAdjusted
    ),
    fundCnpjNormalized: offer.fundCnpj || '',
    productIdNormalized: offer.productId?.toString() || '',
    riskRatingNormalized: offer.riskRating || '',
    productCategoryNormalized: offer.productCategory || '',
    productNameNormalized: offer.productName || '',
    administrationFeeNormalized: offer.administrationFee || '',
    quotationRedemptionTermFormattedNormalized: offer.quotationRedemptionTermFormatted || '',
    riskRatingLevelNormalized: String(offer.riskRatingLevel || 0),
    inputApplicationAmountNormalized: Utils.Mask.Money(offer.applicationMinimumAmount),
    minimumMovimentationNormalized: offer.hasPosition
      ? offer.applicationMinimumAmount
      : offer.participateMinimumAmount,
    minimumMovimentationFormatted: Utils.Mask.Money(
      offer.hasPosition ? offer.applicationMinimumAmount : offer.participateMinimumAmount
    ),
    isReaplicationNormalized: !!offer.hasPosition,
    closedFundNormalized: closedFundForInvestmentStatus.includes(offer.fundStatusId || 0),
    captions: getProductCaptions(offer as DynamicProductCaptionsGetter),
  } as FundOfferEntityNormalized;
};

export const normalizeFundsOffers = (
  data?: ResponsesFundOfferResponse[] | null
): FundOfferEntityNormalized[] => {
  if (!data) {
    return [];
  }

  return data.map(({ offer }) => normalizeFundOffer(offer));
};

export const resolveProductBlocked = (
  productBlocks: ResponsesProductBlockResponse[] | null | undefined,
  accountId?: number
): boolean => {
  if (!productBlocks?.length) {
    return false;
  }

  const block =
    accountId != null
      ? productBlocks.find((item) => item.accountId === accountId)
      : productBlocks[0];

  return block?.blocked === true;
};

const mapFundOfferV2ToFundOffer = (
  offer: ResponsesFundOfferV2Response,
  accountId?: number
): Partial<FundOfferBaseEntity> => {
  const productName = offer.financialInstrumentName || '';

  return {
    ...offer,
    productId: String(offer.productId ?? offer.financialInstrumentId),
    productName,
    productDisplay: productName,
    productCategory: offer.category || offer.subCategory || '',
    productSubCategory: offer.subCategory,
    fundCnpj: offer.corporateTaxpayerRegistry,
    administrationFee: offer.fundAdministrationFeeName,
    riskRating: offer.riskProfileName,
    riskRatingLevel: offer.riskSuitability?.risk ?? offer.riskProfileId,
    profitabilityTwelveMonthsAdjusted: offer.twelveMonths,
    qualifiedInvestor: offer.isQualifiedInvestorOnly,
    professionalInvestor: offer.isProfessionalInvestorOnly,
    fundStatusId: offer.financialInstrumentFundStatusId,
    quotationRedemptionTermFormatted: offer.quotationRedemptionTerm || '',
    earlyRedemptionFee: offer.hasEarlyRedemptionFee,
    earlyRedemptionFeeValue: offer.earlyRedemptionFee,
    blocked: resolveProductBlocked(offer.productBlocks, accountId),
  } as Partial<FundOfferBaseEntity>;
};

export const normalizeFundsOffersV2 = (
  data?: ResponsesFundOfferV2Response[] | null,
  accountId?: number
): FundOfferEntityNormalized[] => {
  if (!data) {
    return [];
  }

  return data.map((offer) => normalizeFundOffer(mapFundOfferV2ToFundOffer(offer, accountId)));
};

export const normalizeFundsFiltersFromMeta = (
  meta?: MetaFilterResponseFundFiltersOffersFilters
): DynamicCustomFiltersProps['filters'] => {
  if (!meta) {
    return [];
  }

  const subCategories = metaFilterToOptions(meta.filter.categoryTypes);
  const subCategoryFilter = normalizeCustomFilter('productCategory', 'Subcategoria', subCategories);

  const filtersWithValues = [subCategoryFilter].filter(filterHasOptions);

  return filtersWithValues;
};

export const normalizeFundsFilters = (
  data?: ResponsesFundOfferCategoriesResponse[] | ResponsesFundCategoriesByAdvisorResponse[] | null
): DynamicCustomFiltersProps => {
  if (!data) {
    return { filters: [] };
  }

  const subCategoryFilter = normalizeCustomFilter('productCategory', 'Subcategoria', data);

  const filtersWithValues = [subCategoryFilter].filter(filterHasOptions);

  return { filters: filtersWithValues };
};

export const normalizeFundOfferDetail = (
  data?: ResponsesFundOfferDetailsResponse | null
): FundOfferDetailEntityNormalized => {
  if (!data) {
    throw new Error('');
  }

  return {
    ...data,
    initials: data?.suitabilityProfileName || '--',
    title: data?.productName || '--',
    isMarketClosed: closedFundForInvestmentStatus.includes(data.fundStatusId || 0),
    marketStatus: data?.marketStatusMessage || '--',
    captions: getProductCaptions(data),
  };
};

export const normalizeRedemptionsFund = (
  redemptions: ResponsesFundRedemptionResponse[]
): FundRedemptionOfferNormalized[] => {
  const data = redemptions?.flatMap(({ portfolio }) => {
    const { portfolioId, funds = [] } = portfolio;

    const getAttentionMessage = (fund: ResponsesRedemptionFundDetailsResponse) => {
      const timeLimit = fund.timeLimitToTransaction?.substring(0, 5) || '';
      if (!timeLimit) {
        return '';
      }
      const timeLimitNormalized = timeLimit.replace(':', 'h');
      return `O resgate solicitado após as ${timeLimitNormalized} só é concluído no próximo dia útil. Lembrando que o valor pode sofrer alterações.`;
    };

    return funds?.map((fund) => ({
      ...fund,
      portfolioId,
      productIdFormatted: `${fund.productId}-${portfolioId}`,
      productIdNormalized: `${fund.productId}-${portfolioId}`,
      productName: fund.fundName || '--',
      amountToRedemption: fund.grossTotal || 0,
      expectedIR: fund.expectedIR || 0,
      expectedIOF: fund.expectedIOF || 0,
      estimatedIncomeTax: Utils.Mask.Money(fund.expectedIR || 0),
      estimatedNetValue: Utils.Mask.Money(fund.netTotal || 0),
      quotationDate: fund.quotationDate ? fund.quotationDate.toString() : '',
      quotationDateFormatted: Utils.Mask.DateFormat(fund.quotationDate, 'dd/MM/yyyy'),
      liquidationDateFormatted: Utils.Mask.DateFormat(fund.liquidationDate, 'dd/MM/yyyy'),
      closedFundNormalized: closedFundForRedemptionStatus.includes(fund.fundStatusId || 0),
      quotationAndLiquidationNormalized: fund.quotationAndLiquidation || '',
      attentionMessage: getAttentionMessage(fund),
    }));
  });

  return data as FundRedemptionOfferNormalized[];
};

export const normalizeFundComparison = (
  data?: ResponsesFundComparisonResponse | null
): FundComparatorResultNormalized => {
  if (!data) {
    return {
      indexes: [],
      products: [],
    };
  }

  const normalizedProducts: FundComparatorResultNormalized['products'] = [];
  if (data?.funds) {
    data.funds.forEach((fund) => {
      if (fund.quotations) {
        fund.quotations.forEach((quotation) => {
          normalizedProducts.push({
            id: fund.fundId.toString(),
            productName: fund.fundName || `Fund ${fund.fundId}`,
            date: quotation.date,
            dateNormalized: Utils.Mask.DateFormat(quotation.date, 'MMM/yy'),
            value: quotation.percentageVariation,
            valueNormalized: Utils.Mask.Percentage(quotation.percentageVariation),
          });
        });
      }
    });
  }

  const normalizedIndexes: FundComparatorResultNormalized['indexes'] = [];
  if (data?.benchmarks) {
    data.benchmarks.forEach((benchmark) => {
      if (benchmark.quotations) {
        benchmark.quotations.forEach((quotation) => {
          normalizedIndexes.push({
            id: benchmark.benchmarkId.toString(),
            indexName: benchmark.benchmarkName || `Benchmark ${benchmark.benchmarkId}`,
            date: quotation.date,
            dateNormalized: Utils.Mask.DateFormat(quotation.date, 'MMM/yy'),
            value: quotation.percentageVariation,
            valueNormalized: Utils.Mask.Percentage(quotation.percentageVariation),
          });
        });
      }
    });
  }

  return {
    indexes: normalizedIndexes,
    products: normalizedProducts,
  };
};
